<template>
  <ul id="sidebar-menu">
    <li>
      <!-- A group header toggles; it does not navigate. A parent that does both is ambiguous
           (open me, or go to me?) and worse on touch, where there is no hover to disambiguate.
           The destination lives in an explicit first child instead. -->
      <button
        v-if="children.length"
        type="button"
        class="menu-item tw-flex tw-items-center tw-gap-4 tw-py-3 tw-px-4 tw-rounded-xl tw-cursor-pointer tw-transition-all tw-duration-200"
        :class="[
          groupActive 
            ? 'tw-bg-[#EEF4FF] tw-text-[#4D84FF]' 
            : 'tw-text-[#64748B] hover:tw-bg-[#F1F5F9] hover:tw-text-[#334155]'
        ]"
        style="width: 100%"
        :aria-expanded="String(open)"
        @click="open = !open"
      >
        <SidebarIcon :title="title" />
        <span class="tw-text-sm tw-font-medium">{{ title }}</span>
        <svg
          class="tw-ml-auto tw-transition-transform tw-duration-200"
          :class="{ 'tw-rotate-90': open }"
          width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"
        >
          <path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <router-link
        v-else
        :to="href"
        class="menu-item tw-flex tw-items-center tw-gap-4 tw-py-3 tw-px-4 tw-rounded-xl tw-cursor-pointer tw-transition-all tw-duration-200"
        :class="[
          focus === true 
            ? 'tw-bg-[#EEF4FF] tw-text-[#4D84FF]' 
            : 'tw-text-[#64748B] hover:tw-bg-[#F1F5F9] hover:tw-text-[#334155]'
        ]"
      >
        <SidebarIcon :title="title" />

        <span class="tw-text-sm tw-font-medium">
          {{ title }}
        </span>
      </router-link>

      <ul v-if="children.length && open" class="tw-mt-1 tw-space-y-1 tw-pl-12">
        <li v-for="child in children" :key="child.href">
          <router-link
            :to="child.href"
            class="tw-block tw-py-2 tw-px-3 tw-rounded-lg tw-text-sm tw-transition-colors tw-duration-200"
            :class="isChildActive(child)
              ? 'tw-text-[#4D84FF] tw-font-medium'
              : 'tw-text-[#64748B] hover:tw-bg-[#F1F5F9] hover:tw-text-[#334155]'"
          >
            {{ child.title }}
          </router-link>
        </li>
      </ul>
    </li>
  </ul>
</template>

<script>
import SidebarIcon from "@/components/navigation/SidebarIcon.vue";

export default {
  name: "SidebarMenu",

  components: { SidebarIcon },

  props: {
    title: { type: String, default: () => "", required: true },
    // Not required once a group carries children: the header toggles, it is not a destination.
    href: { type: String, default: () => "" },
    focus: { type: Boolean, default: () => false },
    // [{ title, href }] — presence of these turns this item into a collapsible group.
    children: { type: Array, default: () => [] },
  },

  data() {
    // Open on load when the route is already inside the group, so a deep link does not land
    // the user on a page whose nav entry is hidden.
    return { open: this.routeIsInside() };
  },

  computed: {
    groupActive() {
      return this.focus === true || this.routeIsInside();
    },
  },

  watch: {
    $route() {
      if (this.routeIsInside()) this.open = true;
    },
  },

  methods: {
    routeIsInside() {
      const path = this.$route?.path || "";
      return (this.children || []).some((c) => path === c.href || path.startsWith(c.href + "/"));
    },

    /**
     * Exact match, not startsWith: the children are nested paths, so a prefix test would light
     * up Overview on every page in the group.
     */
    isChildActive(child) {
      return this.$route?.path === child.href;
    },
  },
};
</script>

<style lang="scss" scoped>
#sidebar-menu {
  list-style: none;
  padding: 0;
  margin: 0;
}
</style>
